// Policy status catalog — built-in lifecycle statuses + agency-defined custom
// ones. The built-ins map 1:1 to the backend PolicyStatus enum; custom statuses
// are a reusable agency list (id/label/labelEs/color) stored in
// AgencySetting.policyStatuses and referenced by Policy.customStatus.
//
// Shared module (like policyTypes.js) so PolicyForm, PolicyCard and the filters
// — used from both PoliciesView and ClientDetail — can read the catalog without
// prop-drilling. The custom list is loaded once from agency settings via
// setCustomPolicyStatuses() at app start.

export const BUILTIN_POLICY_STATUSES = [
  { id: 'active',    label: 'Active',       labelEs: 'Activo',            color: '#10b981', builtin: true },
  { id: 'pending',   label: 'Pending',      labelEs: 'Pendiente',         color: '#f59e0b', builtin: true },
  { id: 'grace',     label: 'Grace period', labelEs: 'Período de gracia', color: '#06b6d4', builtin: true },
  { id: 'cancelled', label: 'Cancelled',    labelEs: 'Cancelada',         color: '#ef4444', builtin: true },
];

// Frontend status id ↔ backend PolicyStatus enum (built-ins only).
export const STATUS_FRONT_TO_BACK = { active: 'ACTIVE', pending: 'PENDING', grace: 'GRACE_PERIOD', cancelled: 'CANCELLED' };
export const STATUS_BACK_TO_FRONT = { ACTIVE: 'active', PENDING: 'pending', GRACE_PERIOD: 'grace', CANCELLED: 'cancelled', LAPSED: 'cancelled', EXPIRED: 'cancelled', INACTIVE: 'cancelled' };

/** Sin acentos, en minusculas y con un solo espacio: para COMPARAR nombres. */
const limpio = (t) => String(t || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Nombres que dicen que la poliza SE CAYO.
const NOMBRE_DE_BAJA = /cancel|anulad|\bbaja\b|caduc|vencid|lapse|expir|terminad|inactiv/;
// ...y los que solo lo ANUNCIAN. Una poliza "pendiente de cancelar" o "en
// riesgo de baja" sigue viva y es justo la que hay que atender: esconderla
// seria peor que el fallo que esto arregla. "Baja prioridad" y "prima baja"
// entran aqui por la misma razon: hablan de otra cosa.
const TODAVIA_NO = /solicit|pendiente|por cancelar|a cancelar|en proceso|tramit|request|pending|riesgo|aviso|amenaz|baja prioridad|baja comision|prima baja|baja prima/;

/**
 * ¿Este NOMBRE de estatus significa que la poliza esta de baja?
 *
 * Los cuatro estatus de fabrica se conocen por su id; los PROPIOS de cada
 * agencia no, y lo unico que dice que son es como se llaman. Una agencia que
 * crea su propia "Cancelada" espera que cuente como cancelada, no como una
 * etiqueta de adorno sobre una poliza viva (Denisse, 2026-09-15: importo una
 * lista entera de polizas del 2025 canceladas y siguieron saliendo en Activas).
 */
export function esNombreDeBaja(texto) {
  const t = limpio(texto);
  if (!t) return false;
  if (TODAVIA_NO.test(t)) return false;
  return NOMBRE_DE_BAJA.test(t);
}

/**
 * ¿Esta poliza esta cancelada? "inactive" es la forma vieja del formulario
 * antiguo y significa lo mismo, asi que cuenta.
 *
 * Vive aqui —y no repetido en cada pantalla— porque de esto depende dinero: en
 * Comisiones por Poliza decide si los meses se cuentan hasta la RENOVACION o
 * hasta la CANCELACION (Lissette 2026-09-11: *"es para saber cuantos meses esa
 * poliza dio dinero"*). Con una copia por pantalla, una contaria 12 meses de
 * una poliza que se cancelo a los 4.
 *
 * Un estatus PROPIO de la agencia se resuelve por su NOMBRE en el catalogo: el
 * id es un `cs_...` que no significa nada, y el enum del servidor lo deja a
 * proposito en ACTIVE (ver `estadoParaElServidor`), asi que sin mirar el nombre
 * una "Cancelada" de la agencia cuenta como poliza viva en TODAS partes —en la
 * pestaña Activas, en los contadores y en los meses que dio dinero.
 */
export const esEstatusCancelado = (id) => {
  const s = String(id || '').trim().toLowerCase();
  if (s === 'cancelled' || s === 'inactive') return true;
  if (!s || BUILTIN_IDS.has(s)) return false;
  // Estatus propio: manda su nombre, no su id. Se mira tambien el catalogo de
  // PAGOS porque un estatus movido alli sigue marcado en polizas (ver abajo).
  const propio = [..._custom, ..._payment].find((x) => String(x && x.id).toLowerCase() === s);
  if (propio) return esNombreDeBaja(propio.labelEs || propio.label || propio.id);
  // Id que no esta en ningun catalogo: los viejos ('lapsed', 'expired') se
  // explican solos, y un `cs_...` de un estatus borrado no dice nada — y no
  // decir nada es NO esconder la poliza.
  return esNombreDeBaja(s);
};

const BUILTIN_IDS = new Set(BUILTIN_POLICY_STATUSES.map((s) => s.id));
export const isBuiltinStatus = (id) => BUILTIN_IDS.has(id);

// Ids que ya no ofrece ningun desplegable pero que hay polizas guardadas con
// ellos (el formulario viejo tenia "inactive", y "prospect" venia de la epoca
// en que prospecto era un estatus de poliza). Sin esta tabla caerian en el
// `|| 'ACTIVE'` de abajo y una poliza cancelada volveria a estar activa sola.
const LEGACY_STATUS_FRONT_TO_BACK = {
  active: 'ACTIVE', pending: 'PENDING', prospect: 'PENDING',
  lapsed: 'LAPSED', cancelled: 'CANCELLED', expired: 'EXPIRED',
  inactive: 'CANCELLED',
};

/**
 * COMO SE CONVIERTE UN ESTATUS DE LA PANTALLA EN LAS DOS COLUMNAS DEL SERVIDOR.
 *
 * El servidor guarda el estatus en dos sitios: `status` (el enum de siempre) y
 * `customStatus` (el id del estatus propio de la agencia). Uno de los dos
 * manda, nunca los dos: un estatus propio deja el enum en ACTIVE para que las
 * cuentas del ciclo de vida sigan sumando, y uno de los de serie limpia el
 * propio.
 *
 * LA EXCEPCION, Y ES LA QUE COSTO EL FALLO: ese ACTIVE "neutral" vale para un
 * "Sometida" o un "Kit enviado" —tramites de una poliza VIVA—, pero no para un
 * estatus propio que se llama "Cancelada". Ahi el enum acaba diciendo lo
 * contrario de lo que dice la etiqueta, y el enum es el que leen los reportes,
 * los avisos de renovacion y el estado del cliente. Cuando el NOMBRE dice baja,
 * el enum dice baja (esNombreDeBaja).
 *
 * Vive aqui —y no dentro del guardado de la poliza— porque desde el 2026-09-11
 * hay DOS sitios que mandan un estatus: el formulario de una poliza y el cambio
 * EN LOTE (Luis Rodriguez). Con una copia en cada uno, el dia que se anada un
 * estatus nuevo uno de los dos se queda sin enterarse.
 */
export function estadoParaElServidor(id) {
  const valor = String(id || 'active');
  if (isBuiltinStatus(valor)) {
    return { status: STATUS_FRONT_TO_BACK[valor] || 'ACTIVE', customStatus: null };
  }
  // Lo que no es de serie es propio de la agencia, y va en `customStatus`. Los
  // ids viejos ademas aciertan su enum por la tabla de arriba en vez de caer
  // todos en ACTIVE; se les deja tambien el id en `customStatus` —igual que
  // antes de sacar esto a un sitio comun— para que la poliza siga mostrando
  // exactamente el estatus con el que estaba guardada.
  return {
    status: LEGACY_STATUS_FRONT_TO_BACK[valor.toLowerCase()]
      || (esEstatusCancelado(valor) ? 'CANCELLED' : 'ACTIVE'),
    customStatus: id ? valor : null,
  };
}

let _custom = []; // agency-defined [{id,label,labelEs,color}]

export function setCustomPolicyStatuses(list) {
  _custom = (Array.isArray(list) ? list : []).filter((s) => s && s.id && (s.label || s.labelEs));
}
export function getCustomPolicyStatuses() { return _custom.slice(); }

// Estados propios del PAGO del mes (modulo Pagos del Mes). Viven en su propio
// catalogo -- AgencySetting.paymentStatuses -- y NO se ofrecen como estado de
// POLIZA: son dos preguntas distintas ("como va el tramite de la poliza" vs
// "como pago este mes"), y mezclarlas fue justo lo que confundio a la agencia
// (Lissette 2026-08-18: "Prima $0" y "Pago Procesado" saliendo en Editar Poliza).
//
// Se registran aqui SOLO para poder LEERLOS. Una poliza marcada con uno de esos
// ids antes de la separacion tiene que seguir mostrando su nombre y su color, no
// un id crudo; por eso getPolicyStatusMeta cae aqui y getAllPolicyStatuses --que
// es la lista de OPCIONES-- no.
let _payment = [];

export function setPaymentStatusCatalog(list) {
  _payment = (Array.isArray(list) ? list : []).filter((s) => s && s.id && (s.label || s.labelEs));
}
export function getPaymentStatusCatalog() { return _payment.slice(); }

export function getAllPolicyStatuses() {
  return [
    ...BUILTIN_POLICY_STATUSES,
    ..._custom.map((s) => ({ id: s.id, label: s.label || s.labelEs, labelEs: s.labelEs || s.label, color: s.color || '#64748b', builtin: false })),
  ];
}

// Resolve a status id (built-in or custom) to a display { id, label, color }.
// Unknown ids (e.g. a custom status that was deleted) fall back to a neutral pill.
export function getPolicyStatusMeta(id, lang) {
  const all = getAllPolicyStatuses();
  const s = all.find((x) => x.id === id);
  if (!s) {
    // Solo para LEER: un estatus que ahora vive en el catalogo de PAGOS sigue
    // mostrandose con su nombre y su color donde ya estuviera marcado.
    const p = _payment.find((x) => x.id === id);
    if (p) return { id: p.id, label: lang === 'es' ? (p.labelEs || p.label) : (p.label || p.labelEs), color: p.color || '#64748b', builtin: false };
    return { id: id || 'active', label: id || (lang === 'es' ? 'Activo' : 'Active'), color: '#64748b', builtin: false };
  }
  return { id: s.id, label: lang === 'es' ? (s.labelEs || s.label) : s.label, color: s.color, builtin: s.builtin };
}
